import * as React from "react";
import { useAuth } from "src/components/providers/auth-context";
import request from "src/utils/request";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";

const SprintsContext = React.createContext<{ allProjects?: { projects: [] } }>({});
SprintsContext.displayName = "SprintsContext";

const SprintsContextProvider = (props) => {
    const { isDemo } = useAuth();
    const { activeIntegrations } = useActiveIntegrations();
    const [selectedSprint, setSelectedSprint] = React.useState("");
    const [allSprints, setAllSprints] = React.useState([]);
    const [allProjects, setAllProjects] = React.useState<{ projects: { projectKey: string }[] }>({
        projects: [],
    });
    const isJiraIntegrated =
        activeIntegrations &&
        activeIntegrations.data &&
        activeIntegrations.data.integrations.find((int) => int.name === "jira").connected;
    const isNotionIntegrated =
        activeIntegrations &&
        activeIntegrations.data &&
        activeIntegrations.data.integrations.find((int) => int.name === "notion").connected;

    const getSprints = async () => {
        let response;
        let projectRes;
        if (isJiraIntegrated || isNotionIntegrated) {
            [response, projectRes] = await Promise.all([
                request(`/api/data/fundamentals/v1/jira-sprints`, { method: "GET" }),
                request(`/api/data/fundamentals/v1/jira-projects`, { method: "GET" }),
            ]);
        }
        if (isDemo || (!isJiraIntegrated && !isNotionIntegrated)) {
            // @ts-expect-error: Ignore TS1323 for dynamic import
            response = (await import("./mock-data/work-fundamentals/jira-sprints")).default;
            // @ts-expect-error: Ignore TS1323 for dynamic import
            projectRes = (await import("./mock-data/work-fundamentals/jira-projects")).default;
        }
        setAllSprints(response.filter((sprint) => sprint.startDate && sprint.endDate));
        setSelectedSprint(response[0]);
        if (projectRes && projectRes.projects && projectRes.projects.length > 0) {
            setAllProjects({ projects: [...projectRes.projects] });
        } else {
            setAllProjects({ projects: [] });
        }
    };

    React.useEffect(() => {
        if (activeIntegrations.status === "SUCCESS") getSprints();
    }, [activeIntegrations.status]);

    const handleSprintChange = (newSelectedSprint) => {
        setSelectedSprint(newSelectedSprint);
    };

    const value = React.useMemo(
        () => ({ handleSprintChange, selectedSprint, allSprints, allProjects }),
        [handleSprintChange, selectedSprint, allSprints, allProjects]
    );

    return <SprintsContext.Provider value={value} {...props} />;
};

const useSprints = () => {
    const context = React.useContext(SprintsContext);
    if (context === undefined) {
        throw new Error(`useSprints must be used within a SprintsProvider`);
    }
    return context;
};

export { SprintsContextProvider, useSprints };
